import React, { memo } from "react";
import { Box, Grid, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Button } from "@shared/components";
import InvestorRequestFormTitle from "@investorRequest/components/InvestorRequestFormTitle";
import { ReactComponent as UploadIcon } from "@assets/svg/clip.svg";
import { ReactComponent as PencilIcon } from "@assets/svg/pencil.svg";

interface Props {
  onFillManually(): void;
  onUploadDocument(): void;
}

interface OptionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onSelect(): void;
  classes: ReturnType<typeof useStyles>;
}

const useStyles = makeStyles((theme) => ({
  highlightedSection: {
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(2),
    padding: theme.spacing(5),
    width: "100%",
  },
  bodyText: {
    color: theme.palette.grey[600],
    marginTop: theme.spacing(1),
    letterSpacing: "1.4px",
  },
  optionCard: {
    width: "100%",
    height: "100%",
    "& .MuiButton-root": {
      border: `2px solid ${theme.palette.grey[400]}`,
      borderRadius: theme.spacing(2),
      padding: theme.spacing(6),
      textAlign: "center",
      backgroundColor: theme.palette.common.white,
      transition: "none",
      boxShadow: theme.shadows[1],
      width: "100%",
      minHeight: 200,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      textTransform: "none",
      font: "inherit",
      color: "inherit",
    },
    "& .MuiButton-root:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.common.white,
    },
  },
  optionCardClickable: {
    "& .MuiButton-root": {
      cursor: "pointer",
    },
  },
  optionIcon: {
    width: 20,
    height: 20,
  },
  iconWrapper: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    backgroundColor: theme.palette.grey[200],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    marginBottom: theme.spacing(2),
  },
  optionTitle: {
    fontWeight: 600,
  },
}));

const OptionCard = ({ icon, title, description, onSelect, classes }: OptionCardProps) => (
  <Box className={`${classes.optionCard} ${classes.optionCardClickable}`}>
    <Button type="button" variant="outlined" shape="sharp" fullWidth onClick={onSelect}>
      <Box className={classes.iconWrapper}>{icon}</Box>
      <Typography variant="bodySubhead2" className={classes.optionTitle}>
        {title}
      </Typography>
      <Typography variant="body2" className={classes.bodyText}>
        {description}
      </Typography>
    </Button>
  </Box>
);

const WireInstructionOptionsDialog = ({ onFillManually, onUploadDocument }: Props) => {
  const classes = useStyles();

  return (
    <>
      <div data-testid="wire-instructions-options-dialog" />
      <Grid item xs={12}>
        <Box className={classes.highlightedSection}>
          <InvestorRequestFormTitle text="How would you like to provide your wire information?" />
          <Typography variant="body2" className={classes.bodyText}>
            You can upload a document with your bank details or fill in the form manually.
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={4} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <OptionCard
              classes={classes}
              icon={<UploadIcon className={classes.optionIcon} />}
              title="Upload a Document"
              description="PDF, image, or bank letter"
              onSelect={onUploadDocument}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <OptionCard
              classes={classes}
              icon={<PencilIcon className={classes.optionIcon} />}
              title="Fill in Manually"
              description="Enter your wire details by hand"
              onSelect={onFillManually}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default memo(WireInstructionOptionsDialog);
